import { useEffect, useMemo, useRef, useState } from 'react'
import { formatMoney } from '../lib/format.js'

const MAX_RENDERED = 50

export default function ItemPicker({
  mode,
  items,
  value,
  onSelect,
  onClear,
  onClose,
  open,
  recipesByItemId,
  onNavigateToItems,
  errorMessage,
  busy,
}) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef(null)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 150)
    return () => clearTimeout(id)
  }, [query])

  useEffect(() => {
    if (mode === 'modal' && open) {
      setQuery('')
      setDebouncedQuery('')
      setHighlight(0)
    }
  }, [mode, open])

  useEffect(() => {
    if (mode === 'modal' && open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [mode, open])

  // Inline mode intentionally does NOT autofocus on mount: stealing
  // focus from the parent form's primary input (e.g. RecipeBuildModal's
  // "Dish description" textarea) is a worse UX than asking the manager
  // to tap the search field. Focus is re-established when the user
  // explicitly Clears a previously-picked value (see Clear button below).

  const sellableItems = useMemo(() => {
    return (items || []).filter(
      (i) => Number(i?.sale_price || 0) > 0 && i?.active === true
    )
  }, [items])

  const filteredItems = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase()
    if (!q) return sellableItems
    return sellableItems.filter((i) =>
      (i.name || '').toLowerCase().includes(q)
    )
  }, [sellableItems, debouncedQuery])

  const renderedItems = useMemo(
    () => filteredItems.slice(0, MAX_RENDERED),
    [filteredItems]
  )

  useEffect(() => {
    setHighlight((h) => {
      if (renderedItems.length === 0) return 0
      if (h >= renderedItems.length) return renderedItems.length - 1
      return h
    })
  }, [renderedItems])

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) =>
        renderedItems.length === 0 ? 0 : Math.min(h + 1, renderedItems.length - 1)
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = renderedItems[highlight]
      if (item) onSelect?.(item)
    } else if (e.key === 'Escape') {
      if (mode === 'modal') {
        e.preventDefault()
        onClose?.()
      } else {
        if (query) {
          e.preventDefault()
          setQuery('')
        }
      }
    }
  }

  if (mode === 'modal' && !open) return null

  if (mode === 'inline' && value) {
    const picked = (items || []).find((i) => i.id === value)
    if (picked) {
      return (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
          <div className="min-w-0 flex-1 text-sm">
            <span className="text-emerald-900/70">Picked: </span>
            <span className="truncate font-medium text-emerald-900">{picked.name}</span>
            <span className="ml-2 text-emerald-900/70 tabular-nums">
              {formatMoney(picked.sale_price)}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              onClear?.()
              // The chip unmounts and the search input mounts on the
              // next render; focus once the input element exists.
              requestAnimationFrame(() => {
                inputRef.current?.focus()
              })
            }}
            className="min-h-[36px] rounded-lg px-2 text-sm font-medium text-emerald-900/80 hover:bg-emerald-100"
          >
            Clear
          </button>
        </div>
      )
    }
  }

  const announcement = `${filteredItems.length} ${filteredItems.length === 1 ? 'item matches' : 'items match'}`
  const noItems = sellableItems.length === 0
  const noMatches = !noItems && filteredItems.length === 0

  const list = (
    <>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search items by name"
          className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
        />
      </div>

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>

      {noItems ? (
        <div className="rounded-xl border border-dashed border-black/15 px-4 py-5 text-sm text-black/65">
          <div>No items with sale prices yet. Add items in Items → New Item first.</div>
          {onNavigateToItems && (
            <button
              type="button"
              onClick={() => onNavigateToItems()}
              className="mt-3 min-h-[44px] rounded-lg border border-blue-600 bg-white px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
            >
              Go to Items
            </button>
          )}
        </div>
      ) : noMatches ? (
        <div className="rounded-xl border border-dashed border-black/15 px-4 py-5 text-sm text-black/65">
          No items match "{debouncedQuery}".
        </div>
      ) : (
        <ul
          className={
            mode === 'modal'
              ? 'divide-y divide-black/5 overflow-y-auto rounded-xl border border-black/10 bg-white'
              : 'max-h-72 divide-y divide-black/5 overflow-y-auto rounded-xl border border-black/10 bg-white'
          }
        >
          {renderedItems.map((it, idx) => {
            const existing = recipesByItemId?.get?.(it.id) || []
            const isHighlight = idx === highlight
            return (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => onSelect?.(it)}
                  onMouseEnter={() => setHighlight(idx)}
                  className={`flex w-full min-h-[44px] items-center justify-between gap-3 px-4 py-3 text-left ${
                    isHighlight ? 'bg-blue-50 ring-2 ring-amber-500' : 'hover:bg-black/[0.03]'
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate text-base font-medium">
                    {it.name}
                  </span>
                  <span className="shrink-0 text-sm text-black/60 tabular-nums">
                    {formatMoney(it.sale_price)}
                  </span>
                </button>
                {existing.length > 0 && (
                  <div className="border-t border-amber-200 bg-amber-50/60 px-4 py-2 text-xs text-amber-900">
                    Already linked to recipe "{existing[0].name}". Linking will mean the most recent recipe wins in Menu Engineering.
                  </div>
                )}
              </li>
            )
          })}
          {filteredItems.length > MAX_RENDERED && (
            <li className="px-4 py-2 text-xs text-black/55">
              Showing first {MAX_RENDERED} of {filteredItems.length}. Keep typing to narrow.
            </li>
          )}
        </ul>
      )}
    </>
  )

  if (mode === 'modal') {
    return (
      <div className="fixed inset-0 z-40">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute inset-0 bg-black/50 animate-fade-in"
        />
        <div
          className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[80vh] overflow-hidden rounded-t-2xl bg-white shadow-2xl flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Link recipe to menu item"
        >
          <div className="sticky top-0 z-10 bg-white pt-2">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
            <div className="flex items-center justify-between px-4 py-3">
              <h2 className="text-xl font-medium">Link to menu item</h2>
              <button
                type="button"
                onClick={onClose}
                className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              >
                Cancel
              </button>
            </div>
            {errorMessage && (
              <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
                {errorMessage}
              </div>
            )}
          </div>
          <div className="space-y-3 overflow-y-auto px-4 pb-4">
            {list}
            {busy && (
              <div className="text-center text-sm text-black/60">Linking{'…'}</div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return <div className="space-y-2">{list}</div>
}
